import { useMutation } from '@apollo/client';
import { CREATE_CREATIVE } from '../../graphql/mutations/creativeMutations';

export const sanitizeCreativeInput = (input = {}) => {
  const cleaned = { ...input };
  Object.keys(cleaned).forEach((key) => {
    // Remove null, undefined, or empty string values
    if (cleaned[key] === undefined || cleaned[key] === null || cleaned[key] === '') {
      delete cleaned[key];
    }
    // Remove empty arrays
    if (Array.isArray(cleaned[key]) && cleaned[key].length === 0) {
      delete cleaned[key];
    }
  });
  return cleaned;
};

export const buildCreateCreativeInput = (creativeData = {}) => sanitizeCreativeInput({
  id: creativeData.id,
  userID: creativeData.userID,
  creativeType: creativeData.creativeType,
  visualTypes: creativeData.visualTypes, // Array of VisualType
  status: creativeData.status || 'DRAFT', // Default to DRAFT
  profilePicture: creativeData.profilePicture,
  displayName: creativeData.displayName,
  jobTitle: creativeData.jobTitle,
  description: creativeData.description,
  skills: creativeData.skills // Array of strings
});

/**
 * Hook to create a new Creative profile
 * @returns {object} - { handleCreateCreative, loading, error }
 */
export const useCreateCreative = () => {
  const [createCreativeMutation, { loading, error }] = useMutation(CREATE_CREATIVE, {
    refetchQueries: ['CreativesByUserID', 'GetCreative'],
    awaitRefetchQueries: true
  });

  const handleCreateCreative = async (creativeData) => {
    try {
      if (!creativeData?.userID) {
        throw new Error('Dein Nutzerkonto konnte nicht zugeordnet werden. Bitte lade die Seite neu und versuche es erneut.');
      }

      // Creative type is required (should be "music", "foto", or "video")
      if (!creativeData?.creativeType || creativeData.creativeType === "") {
        throw new Error('Bitte wähle einen Kreativbereich aus.');
      }

      if (!creativeData?.profilePicture) {
        throw new Error('Bitte lade ein Profilbild hoch.');
      }

      if (!creativeData?.displayName) {
        throw new Error('Bitte gib einen Anzeigenamen ein.');
      }

      if (!creativeData?.jobTitle) {
        throw new Error('Bitte gib eine Jobbezeichnung ein.');
      }

      const input = buildCreateCreativeInput(creativeData);

      const { data: creativeResponse } = await createCreativeMutation({
        variables: { input }
      });

      return creativeResponse?.createCreative;
    } catch (error) {
      console.error('Error creating creative:', error);
      throw error;
    }
  };

  return {
    handleCreateCreative,
    loading,
    error
  };
};
